// Co-located styles for FillInTheBlanksActivityMaterialContent.
// Prefix: fitb2-  (registered in STYLING.md library table)
// Built on Playful Frost design tokens only — NO Tailwind utility classes.
export const fillInTheBlanksActivityMaterialContentStyles = `
  /* ── root ────────────────────────────────────────────────────────── */
  .fitb2-root { display: flex; flex-direction: column; gap: var(--space-4); }

  /* ── sentence area: flowing blank slots ──────────────────────────── */
  .fitb2-sentence {
    font-size: var(--fs-xl);
    line-height: 2.6;
    color: var(--text-secondary);
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin-bottom: 4px;
  }

  /* A single blank slot (inline-flex). Always inside .fitb2-blank-col when
     is-wrong so the fix-below label can live beneath it. */
  .fitb2-blank {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 110px;
    height: 34px;
    padding: 0 12px;
    cursor: pointer;
    border-radius: var(--rad-catchup-small) var(--rad-catchup-small) 0 0;
    font-size: var(--fs-base);
    font-weight: 600;
    white-space: nowrap;
    transition: background 150ms, border-color 150ms;
  }
  .fitb2-blank.empty {
    border-bottom: 2px dashed var(--catchup-blue-300);
    color: var(--text-muted);
    font-size: var(--fs-sm);
    font-weight: 500;
    opacity: 0.7;
  }
  .fitb2-blank.is-target {
    border-bottom: 2px solid var(--catchup-blue);
    background: var(--brand-50);
    color: var(--brand-dark);
    opacity: 1;
  }
  .fitb2-blank.filled {
    font-weight: 700;
    color: var(--brand-dark);
    background: var(--brand-50);
    border-bottom: 2px solid var(--catchup-blue);
  }
  .fitb2-blank.is-correct {
    color: var(--accent-lime-text, var(--color-success-text));
    background: linear-gradient(160deg, rgba(122,203,164,0.42), rgba(94,170,130,0.24));
    border-bottom-color: var(--accent-lime-text, var(--color-success));
  }
  .fitb2-blank.is-wrong {
    color: var(--accent-coral-text, var(--color-error-text));
    background: linear-gradient(160deg, rgba(232,150,128,0.4), rgba(217,97,63,0.22));
    border-bottom-color: var(--accent-coral, var(--color-error));
    text-decoration: line-through;
  }

  /* Left blank — amber, deliberately NOT the red of a wrong word: the learner
     did not answer wrongly, they did not answer. No strike-through: there is
     nothing to strike. */
  .fitb2-blank.is-blank {
    color: var(--color-warning-text);
    background: var(--color-warning-bg);
    border-bottom-color: var(--color-warning);
  }

  /* Column wrapper for wrong answer + correct-answer-below label */
  .fitb2-blank-col {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    vertical-align: middle;
  }
  .fitb2-blank-col .fitb2-blank { /* cancel any extra margin on the inner blank */ }

  /* "✓ correct answer" shown below a wrong blank */
  .fitb2-fix-below {
    font-size: var(--fs-lg);
    font-weight: 700;
    line-height: 1.2;
    color: var(--accent-lime-text, var(--color-success-text));
    white-space: nowrap;
  }

  /* blank number badge (shown in non-interactive / isPreview==false mode) */
  .fitb2-blank-num {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 28px;
    height: 28px;
    border-radius: 50%;
    font-size: var(--fs-sm);
    font-weight: 800;
    color: #fff;
    background: linear-gradient(160deg, var(--catchup-blue), var(--brand-dark));
    box-shadow: inset 0 1.5px 1.5px rgba(255,255,255,0.55), 0 3px 7px -2px rgba(87,194,211,0.55);
    flex-shrink: 0;
  }

  /* answered-text in read-only (non-interactive) mode */
  .fitb2-readonly-answer {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: var(--fs-lg);
    font-weight: 600;
    color: var(--brand-dark);
    padding: 4px 12px;
    border-radius: var(--rad-catchup-full);
    background: var(--brand-50);
    border: 1.5px solid var(--catchup-blue);
  }

  /* ── image-option blank (filled with media) ──────────────────────── */
  .fitb2-img-blank {
    display: inline-block;
    min-width: 80px;
    min-height: 60px;
    border-radius: var(--rad-catchup-medium);
    border: 1.5px dashed var(--catchup-blue-200);
    background: rgba(255,255,255,0.45);
    -webkit-backdrop-filter: blur(8px); backdrop-filter: blur(8px);
    cursor: pointer;
    vertical-align: middle;
    overflow: hidden;
  }
  .fitb2-img-blank.is-target { border-color: var(--catchup-blue); background: var(--brand-50); }
  .fitb2-img-blank.filled { border-style: solid; border-color: var(--catchup-blue); cursor: pointer; }
  .fitb2-img-blank.is-correct { border-color: var(--color-success); }
  .fitb2-img-blank.is-wrong { border-color: var(--color-error); }
  /* Graded image blank: not a drop target, and the frame carries the verdict. */
  .fitb2-img-blank.is-graded { cursor: default; border-width: 2px; }
  .fitb2-img-blank.is-graded.is-correct { background: var(--color-success-bg); }
  .fitb2-img-blank.is-graded.is-wrong { background: var(--color-error-bg); }
  .fitb2-img-blank.is-graded.is-blank { border-color: var(--color-warning); background: var(--color-warning-bg); }
  .fitb2-img-blank.is-graded.is-key { border-style: dashed; border-color: var(--color-success); }
  .fitb2-img-empty {
    display: flex; align-items: center; justify-content: center;
    min-width: 80px; min-height: 60px;
    font-size: var(--fs-base); font-weight: 700; color: var(--color-warning-text);
  }
  .fitb2-img-key { display: inline-flex; flex-direction: column; align-items: center; gap: 2px; }
  /* The same small uppercase tag as .aas-corr-tag. */
  .fitb2-img-key-tag { font-size: var(--fs-xs); font-weight: 800; letter-spacing: 0.04em; text-transform: uppercase; color: var(--color-success-text); }

  /* ── word bank ────────────────────────────────────────────────────── */
  .fitb2-bank-label {
    font-size: var(--fs-sm);
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--text-muted);
    margin-bottom: 8px;
  }
  .fitb2-bank {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }
  .fitb2-word {
    cursor: pointer;
    font-weight: 700;
    font-size: var(--fs-base);
    padding: 9px 16px;
    border-radius: var(--rad-catchup-full);
    background: var(--catchup-white);
    border: 1.5px solid var(--catchup-blue-100);
    color: var(--brand-dark);
    transition: transform 150ms, border-color 150ms, box-shadow 150ms, background 150ms;
    font-family: var(--font-sans);
    line-height: 1;
  }
  .fitb2-word:hover:not(:disabled) { transform: translateY(-1px); border-color: var(--catchup-blue); box-shadow: 0 6px 14px -6px rgba(15,72,89,0.3); }
  .fitb2-word.is-picked { background: var(--brand-50); border-color: var(--catchup-blue); box-shadow: 0 0 0 3px var(--brand-100, rgba(87,194,211,0.22)); }
  .fitb2-word.used { opacity: 0.32; cursor: default; text-decoration: line-through; pointer-events: none; }
  .fitb2-word:disabled { opacity: 0.32; cursor: default; pointer-events: none; }

  /* ── hint text ────────────────────────────────────────────────────── */
  .fitb2-hint {
    font-size: var(--fs-sm);
    color: var(--text-muted);
  }

  /* ── floating drag ghost ──────────────────────────────────────────── */
  .fitb2-drag-ghost {
    position: fixed;
    pointer-events: none;
    z-index: 50;
    opacity: 0.85;
  }
  .fitb2-ghost-chip {
    padding: 9px 16px;
    border-radius: var(--rad-catchup-full);
    background: var(--catchup-white);
    border: 2px solid var(--catchup-blue);
    color: var(--brand-dark);
    font-weight: 700;
    font-size: var(--fs-base);
    box-shadow: 0 10px 20px -6px rgba(15,72,89,0.35);
  }

  /* Responsive: on small screens wrap banks naturally, blanks shrink */
  @media (max-width: 480px) {
    .fitb2-blank { min-width: 72px; padding: 0 8px; }
    .fitb2-word { padding: 7px 12px; font-size: var(--fs-sm); }
  }
`;
